/**
 * /gpu-exchange — sublet idle reserved GPUs to internal teams.
 */
import React, { useEffect, useState } from 'react';
import { Server, Plus, Check, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GPU_TYPES = [
  { value: 'h100', label: 'H100 ($4.10/hr)' },
  { value: 'b200', label: 'B200 ($6.50/hr)' },
  { value: 'a100', label: 'A100 ($2.20/hr)' },
  { value: 'l40s', label: 'L40S ($1.30/hr)' },
];

const GpuExchange = () => {
  const [listings, setListings] = useState([]);
  const [savings, setSavings] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ gpu_type: 'h100', gpu_count: 1, hours_available: 100, region: 'us-east-1', owner_team: 'ml-platform', note: '' });
  const [claimForm, setClaimForm] = useState({});

  const headers = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
  });

  const load = async () => {
    try {
      const [l, s] = await Promise.all([
        fetch(`${API}/gpu-exchange/listings`, { headers: headers() }).then((r) => r.json()),
        fetch(`${API}/gpu-exchange/savings-summary`, { headers: headers() }).then((r) => r.json()),
      ]);
      setListings(l.listings || []);
      setSavings(s);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const postListing = async () => {
    const r = await fetch(`${API}/gpu-exchange/list`, { method: 'POST', headers: headers(), body: JSON.stringify(form) });
    if (r.ok) {
      toast.success('Listing posted');
      setShowNew(false);
      load();
    } else {
      toast.error('Failed to post listing');
    }
  };

  const claim = async (listing_id) => {
    const cf = claimForm[listing_id] || { hours: 1, claiming_team: 'eng-team', purpose: '' };
    const r = await fetch(`${API}/gpu-exchange/claim/${listing_id}`, { method: 'POST', headers: headers(), body: JSON.stringify(cf) });
    if (r.ok) {
      toast.success('Claim recorded — confirm transfer in your cloud console.');
      load();
    } else {
      const e = await r.json();
      toast.error(e.detail || 'Claim failed');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="gpu-exchange-page">
      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3" data-testid="gpu-ex-badge">
              <Server className="w-3 h-3 mr-1.5 inline" /> GPU Internal Capacity Exchange
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>GPU Capacity Exchange</BrandTitle></h1>
            <p className="text-slate-300 mt-1 max-w-2xl">
              Idle H100s on a 1-year reservation? List the spare hours; another internal team claims them.
              Stop paying $4/hr per GPU just to keep the reservation warm.
            </p>
          </div>
          <Button onClick={() => setShowNew(true)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950" data-testid="gpu-ex-new">
            <Plus className="w-4 h-4 mr-1.5" /> Post idle GPUs
          </Button>
        </div>

        {savings && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-emerald-500/10 border-emerald-500/30 shadow-sm">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Recaptured</div>
                <div className="text-3xl font-extrabold text-emerald-300 tabular-nums">${savings.total_recaptured_usd?.toLocaleString() || 0}</div>
                <div className="text-xs text-slate-300 mt-1">{savings.claims_count} claims</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/10 shadow-sm">
              <CardContent className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold">Open listings</div>
                <div className="text-3xl font-extrabold text-white tabular-nums">{savings.open_listings_count || 0}</div>
                <div className="text-xs text-slate-300 mt-1">${(savings.open_listings_value_usd || 0).toLocaleString()} available</div>
              </CardContent>
            </Card>
          </div>
        )}

        {showNew && (
          <Card className="bg-slate-900/60 border-emerald-300 shadow-md mb-6" data-testid="gpu-ex-new-form">
            <CardContent className="p-5 grid grid-cols-2 md:grid-cols-3 gap-3">
              <select value={form.gpu_type} onChange={(e) => setForm({ ...form, gpu_type: e.target.value })} className="rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="gpu-ex-form-type">
                {GPU_TYPES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
              <input type="number" placeholder="GPU count" value={form.gpu_count} onChange={(e) => setForm({ ...form, gpu_count: parseInt(e.target.value, 10) || 1 })} className="rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="gpu-ex-form-count" />
              <input type="number" placeholder="Hours available" value={form.hours_available} onChange={(e) => setForm({ ...form, hours_available: parseInt(e.target.value, 10) || 1 })} className="rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="gpu-ex-form-hours" />
              <input type="text" placeholder="Region (e.g. us-east-1)" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="gpu-ex-form-region" />
              <input type="text" placeholder="Owner team" value={form.owner_team} onChange={(e) => setForm({ ...form, owner_team: e.target.value })} className="rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="gpu-ex-form-team" />
              <input type="text" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="gpu-ex-form-note" />
              <div className="col-span-full flex justify-end gap-2">
                <Button onClick={() => setShowNew(false)} variant="outline" className="border-white/15">Cancel</Button>
                <Button onClick={postListing} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950" data-testid="gpu-ex-form-submit">Post listing</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-slate-900/60 border-white/10 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-white/10">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              Open exchange listings
            </h2>
          </div>
          {listings.length === 0 ? (
            <div className="px-6 py-12 text-center text-slate-500">
              <Server className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <p>No listings yet. Click <strong>Post idle GPUs</strong> to start the exchange.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-200" data-testid="gpu-ex-listings">
              {listings.map((lst) => {
                const remaining = lst.hours_available - lst.hours_claimed;
                return (
                  <li key={lst.listing_id} className="px-6 py-4 hover:bg-slate-900/60">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-emerald-300">{lst.gpu_count}× {lst.gpu_type.toUpperCase()}</span>
                          <Badge className="bg-slate-900/60 border-white/15 text-slate-200 text-xs">{lst.region}</Badge>
                          {lst.status === 'fully_claimed' && <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 text-xs"><Check className="w-3 h-3 mr-0.5 inline" />Fully claimed</Badge>}
                        </div>
                        <p className="text-xs text-slate-300">
                          Listed by <strong>{lst.owner_team}</strong> · {lst.hours_available}h total · {lst.hours_claimed}h claimed · {remaining}h remaining
                        </p>
                        {lst.note && <p className="text-xs text-slate-500 italic mt-1">"{lst.note}"</p>}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-emerald-300 tabular-nums">${lst.total_value_usd?.toLocaleString()}</div>
                        <div className="text-[10px] text-slate-500">@ ${lst.hourly_list_usd}/hr/gpu</div>
                      </div>
                    </div>
                    {lst.status === 'open' && remaining > 0 && (
                      <div className="flex items-center gap-2 mt-3">
                        <input type="number" placeholder="Hours" max={remaining} className="rounded-lg border border-white/15 px-2 py-1 text-xs w-20" onChange={(e) => setClaimForm({ ...claimForm, [lst.listing_id]: { ...claimForm[lst.listing_id], hours: parseInt(e.target.value, 10) || 1 } })} data-testid={`gpu-ex-claim-hours-${lst.listing_id}`} />
                        <input type="text" placeholder="Your team" className="rounded-lg border border-white/15 px-2 py-1 text-xs" onChange={(e) => setClaimForm({ ...claimForm, [lst.listing_id]: { ...claimForm[lst.listing_id], claiming_team: e.target.value } })} data-testid={`gpu-ex-claim-team-${lst.listing_id}`} />
                        <Button size="sm" onClick={() => claim(lst.listing_id)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs" data-testid={`gpu-ex-claim-btn-${lst.listing_id}`}>Claim hours</Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </main>
    </div>
  );
};

export default GpuExchange;
